import React, { useCallback, useEffect, useRef } from 'react';
import { StockMonitor } from '../../../components/trading/StockMonitor';
import type { WidgetInstanceProps } from '../../../components/grid-dashboard';
import { useOrderTicketNavigation } from '../../order-ticket/navigation/OrderTicketNavigationContext';

/* ── Inner component that assumes context is available ─────────────── */

const StockMonitorInner: React.FC = () => {
  const {
    ticksBySymbol,
    retainSymbolSubscription,
    releaseSymbolSubscription,
  } = useOrderTicketNavigation();

  const retainedSymbolsRef = useRef<Set<string>>(new Set());

  const handleSubscribe = useCallback(
    (symbol: string) => {
      retainedSymbolsRef.current.add(symbol);
      retainSymbolSubscription(symbol);
    },
    [retainSymbolSubscription],
  );

  const handleUnsubscribe = useCallback(
    (symbol: string) => {
      retainedSymbolsRef.current.delete(symbol);
      releaseSymbolSubscription(symbol);
    },
    [releaseSymbolSubscription],
  );

  // Release all retained subscriptions on unmount.
  useEffect(() => {
    const retained = retainedSymbolsRef.current;
    return () => {
      retained.forEach((sym) => releaseSymbolSubscription(sym));
      retained.clear();
    };
  }, [releaseSymbolSubscription]);

  return (
    <StockMonitor
      ticksBySymbol={ticksBySymbol}
      onSubscribe={handleSubscribe}
      onUnsubscribe={handleUnsubscribe}
    />
  );
};

/* ── Error boundary to catch missing context ───────────────────────── */

interface ErrorBoundaryState {
  hasError: boolean;
  message: string;
}

class StockMonitorErrorBoundary extends React.Component<
  { children: React.ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, message: '' };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      message: error.message || 'Unexpected error in Stock Monitor widget.',
    };
  }

  componentDidCatch(error: Error) {
    // eslint-disable-next-line no-console
    console.error('StockMonitorWidget render error:', error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '1rem', color: 'var(--color-error, #e74c3c)' }}>
          <strong>Stock Monitor unavailable</strong>
          <p style={{ marginTop: '0.5rem' }}>
            {this.state.message.includes('OrderTicketNavigationProvider')
              ? 'OrderTicketNavigationContext is not available. Ensure this widget is rendered inside an OrderTicketNavigationProvider.'
              : this.state.message || 'Unable to render Stock Monitor widget.'}
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * Self-contained Stock Monitor widget.
 *
 * Obtains live tick data and symbol subscription helpers from
 * OrderTicketNavigationContext, then delegates all UI logic (symbol list,
 * pending input, add/remove) to the existing StockMonitor component.
 *
 * Tracks every symbol subscription it retains and releases them all on
 * unmount so the ref-counted subscription system stays balanced.
 */
export const StockMonitorWidget: React.FC<WidgetInstanceProps> = (_props) => {
  return (
    <StockMonitorErrorBoundary>
      <StockMonitorInner />
    </StockMonitorErrorBoundary>
  );
};

export default StockMonitorWidget;
